"use client"

import { useState, useEffect, KeyboardEvent } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import dynamic from "next/dynamic";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Download,
  Shield,
  Zap,
  Users,
  TrendingUp,
  MessageCircle,
  Phone,
  Mail

} from "lucide-react";
import { getIcon } from "@/lib/utils";
import Image from "next/image";
import { Footer } from "./Footer";
import iTimePlus from "@/assets/ITime.svg";
import KashAi from "@/assets/KashAI.png";
import LuxFleet from "@/assets/Luxfleet.svg";
import SmartContractManager from "@/assets/SmartContractManager.svg"
import AIAnalytics from "@/assets/ai-analytics2.png";
import { ImageWithFallback } from "@/components/figma/ImageWithFallback";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon"
import { contactInfo } from "@/data/contact"

const ContactPopup = dynamic(() => import("./ContactPopup").then((mod) => mod.ContactPopup), { ssr: false });
const ContactBottomSheet = dynamic(() => import("./ContactBottomSheet").then((mod) => mod.ContactBottomSheet), { ssr: false });


interface Product {
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  features: string[];
}

interface ProductDetailPageProps {
  product: Product;
}

export const ProductDetailPage = ({ product }: ProductDetailPageProps) => {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false)
  const [isContactBottomSheetOpen, setIsContactBottomSheetOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleContactClick = () => {
    if (isMobile) {
      setIsContactBottomSheetOpen(true)
    } else {
      setIsContactPopupOpen(true)
    }
  }

  const handleContactKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleContactClick()
    }
  }

  const productImages = [
    iTimePlus.src, SmartContractManager.src,  KashAi.src,
    AIAnalytics.src,
    LuxFleet.src, // Lavender
   
  ];


  const productIndex = product.title === "iTimePlus" ? 0 :
      product.title === "Smart Contract Manager" ? 1 :
        product.title === "KA$H AI" ? 2 :
          product.title === "PRiSM" ? 3 :
            product.title === "Lavender" ? 4 : 5;

  const benefits = [
    { icon: Zap, title: "Lightning Fast", desc: "Process data 10x faster than traditional methods" },
    { icon: Shield, title: "Enterprise Security", desc: "Bank-grade security with 256-bit encryption" },
    { icon: Users, title: "Team Collaboration", desc: "Real-time collaboration features for teams" },
    { icon: TrendingUp, title: "Smart Analytics", desc: "AI-powered insights and recommendations" }
  ];

  const stats = [
    { number: "99.9%", label: "Uptime", icon: Shield },
    { number: "24/7", label: "Support", icon: MessageCircle }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--color-primary)]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--color-primary)]/10 rounded-full blur-[120px]" />
      </div>
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-950 to-black overflow-hidden border-b border-slate-800/50">
        <div className="absolute inset-0 opacity-10">
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                "radial-gradient(circle at 20% 30%, var(--color-primary) 0%, transparent 50%)",
                "radial-gradient(circle at 80% 70%, var(--color-primary-light) 0%, transparent 50%)",
                "radial-gradient(circle at 20% 30%, var(--color-primary) 0%, transparent 50%)"
              ]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Back Button */}
          <Link href="/products">
            <motion.button
              className="cursor-pointer flex items-center space-x-2 text-white/80 hover:text-white transition-colors duration-300 mb-8 group"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <ArrowLeft className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
              <span className="font-medium">Back to Products</span>
            </motion.button>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Info */}
            <motion.div
              className="text-white"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge
                variant="outline"
                className="border-[var(--color-primary)]/50 text-[var(--color-primary)] bg-white/10 backdrop-blur-sm mb-4"
              >
                AI Solution
              </Badge>

              <h1 className="text-4xl md:text-6xl font-bold mb-4">
                {product.title === "KA$H AI" ? (
                  <>
                    KA<span className="text-[#4ebf8a]">$</span>H AI
                  </>
                ) : (
                  product.title
                )}
              </h1>

              <p className="text-xl text-[var(--color-primary)] font-semibold mb-6">
                {product.subtitle}
              </p>

              <p className="text-lg text-white/90 leading-relaxed mb-8 max-w-lg">
                {product.description}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {stats.slice(0, 4).map((stat, index) => (
                  <motion.div
                    key={index}
                    className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-slate-700/50 hover:border-[var(--color-primary)]/30 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="flex items-center space-x-3">
                      <stat.icon className="h-5 w-5 text-[var(--color-primary)]" />
                      <div>
                        <p className="text-xl font-bold text-white">{stat.number}</p>
                        <p className="text-sm text-white/70">{stat.label}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Action Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {product.title === "KA$H AI" ? (
                  <motion.a
                    href="https://www.kash-ai.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center cursor-pointer bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] hover:from-[var(--color-primary-light)] hover:to-[var(--color-primary)] text-white px-8 py-3 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[var(--color-primary)]/30 font-semibold"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ArrowRight className="mr-2 h-5 w-5" />
                    Visit Our Website
                  </motion.a>
                ) : (
                  <Button
                    onClick={handleContactClick}
                    className="cursor-pointer bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] hover:from-[var(--color-primary-light)] hover:to-[var(--color-primary)] text-white px-8 py-3 rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[var(--color-primary)]/30"
                    size="lg"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Request Demo
                  </Button>
                )}
                {/* <Button 
                  variant="outline"
                  className="border-[#E53935]/50 text-[#E53935] bg-white/90 backdrop-blur-sm hover:bg-[#E53935] hover:text-white hover:border-[#E53935] px-8 py-3 rounded-xl transition-all duration-300 shadow-lg"
                  size="lg"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Video
                </Button> */}
              </motion.div>
            </motion.div>

            {/* Product Image */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-500">
                <ImageWithFallback
                  src={productImages[productIndex]}
                  alt={product.title}
                  className="w-full h-[400px] object-cover"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  priority
                  fetchPriority="high"
                />
                {/* <Image src={productImages[productIndex]} alt={product.title} className="w-full h-[400px] object-cover" /> */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                {/* Floating Icon */}
                <motion.div
                  className="absolute top-6 left-6 w-16 h-16 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] rounded-2xl flex items-center justify-center shadow-lg shadow-[var(--color-primary)]/30 overflow-hidden"
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  {product.title === "PRiSM" ? (
                    <Image
                      src="/icons/PRiSM-Light Mode.svg"
                      alt="PRiSM icon"
                      width={34}
                      height={34}
                      className="h-8 w-8 object-contain"
                    />
                  ) : (() => {
                    const IconComponent = getIcon(product.icon);
                    return <IconComponent className="h-8 w-8 text-white" />;
                  })()}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Complete{" "}
              <span className="bg-gradient-to-r from-[var(--color-primary)] via-[var(--color-primary-light)] to-[var(--color-primary)] bg-clip-text text-transparent">
                Feature Set
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] mx-auto mb-6 rounded-full shadow-lg shadow-[var(--color-primary)]/50"></div>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Discover all the powerful features that make {product.title} the perfect solution for your business needs.
            </p>
          </motion.div>

          {/* All Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.features.map((feature: string, index: number) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-slate-900/90 to-slate-800/90 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 hover:border-[var(--color-primary)]/50 hover:shadow-xl hover:shadow-[var(--color-primary)]/20 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-primary-light)]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 border border-[var(--color-primary)]/30">
                    <CheckCircle className="h-4 w-4 text-[var(--color-primary)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{feature}</h3>
                    <p className="text-sm text-slate-400">
                      Advanced capability designed to enhance your workflow and productivity.
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-20 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl mb-4 text-white">
              Why Choose{" "}
              <span className=" bg-clip-text font-bold">
                {product.title === "KA$H AI" ? (
                  <>
                    KA<span className="text-[#4ebf8a]">$</span>H AI
                  </>
                ) : (
                  product.title
                )}
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] mx-auto mb-6 rounded-full shadow-lg shadow-[var(--color-primary)]/50"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-slate-900/90 to-slate-800/90 backdrop-blur-xl rounded-xl p-8 text-center hover:shadow-xl hover:shadow-[var(--color-primary)]/20 transition-all duration-300 border border-slate-700/50 hover:border-[var(--color-primary)]/50"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-primary-light)]/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[var(--color-primary)]/30">
                  <benefit.icon className="h-8 w-8 text-[var(--color-primary)]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {benefit.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <motion.div
            className="relative rounded-2xl p-12 md:p-16 lg:p-20 text-white text-center bg-slate-900/50 border border-slate-700/50"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
                Ready to Get Started?
              </h2>
              <p className="text-lg md:text-xl mb-12 max-w-2xl mx-auto text-slate-300 leading-relaxed">
                Transform your business with {product.title === "KA$H AI" ? (
                  <>
                    KA<span className="text-[#4ebf8a]">$</span>H AI
                  </>
                ) : (
                  product.title
                )}. Contact our experts for a personalized demo and implementation plan.
              </p>

              <motion.div
                className="flex items-center justify-center space-x-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                {/* WhatsApp Icon */}
                <motion.a
                  href={contactInfo.whatsapp.ksa.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat with us on WhatsApp"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(contactInfo.whatsapp.ksa.href, "_blank");
                  }}
                  className="w-11 h-11 border border-slate-700 hover:border-[#25D366] rounded-full flex items-center justify-center transition-all duration-300 bg-slate-800/50 hover:bg-[#25D366]/10 backdrop-blur-sm cursor-pointer"
                >
                  <WhatsAppIcon className="h-5 w-5 text-slate-400 hover:text-[#25D366] transition-colors duration-300" />
                </motion.a>

                {/* Phone Icon - Opens Popup/Bottom Sheet */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleContactClick();
                  }}
                  onKeyDown={handleContactKeyDown}
                  role="button"
                  tabIndex={0}
                  aria-label="Request a call"
                  className="w-11 h-11 border border-slate-700 hover:border-[var(--color-primary)] rounded-full flex items-center justify-center transition-all duration-300 bg-slate-800/50 hover:bg-[var(--color-primary)]/10 backdrop-blur-sm cursor-pointer"
                >
                  <Phone className="h-5 w-5 text-slate-400 hover:text-[var(--color-primary)] transition-colors duration-300" />
                </motion.div>

                {/* Email Icon */}
                <motion.a
                  href={`mailto:${contactInfo.email}`}
                  aria-label="Email us"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `mailto:${contactInfo.email}`
                  }}
                  className="w-11 h-11 border border-slate-700 hover:border-blue-500 rounded-full flex items-center justify-center transition-all duration-300 bg-slate-800/50 hover:bg-blue-500/10 backdrop-blur-sm cursor-pointer"
                >
                  <Mail className="h-5 w-5 text-slate-400 hover:text-blue-500 transition-colors duration-300" />
                </motion.a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Popup */}
      <ContactPopup
        isOpen={isContactPopupOpen}
        onClose={() => setIsContactPopupOpen(false)}
      />

      {/* Contact Bottom Sheet */}
      <ContactBottomSheet
        isOpen={isContactBottomSheetOpen}
        onClose={() => setIsContactBottomSheetOpen(false)}
      />

      <Footer />
    </div>
  );
};
